import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AuthResponse, RegisterRequest, TwoFactorLoginResponse } from '../types/api';
import api from '../lib/api';
import { messageHandler } from '../services/message-handler.service';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  isSessionExpired: boolean;
  error: string | null;
  isLoading: boolean;
  twoFactorToken: string | null;
  requiresTwoFactor: boolean;
  initializeAuth: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  completeTwoFactorLogin: (twoFactorToken: string, code: string) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: (message?: string) => void;
  refreshAccessToken: () => Promise<string | null>;
  clearError: () => void;
  clearSessionExpired: () => void;
  fetchUserInfo: () => Promise<User | null>;
  clearTwoFactor: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isInitializing: true,
      isSessionExpired: false,
      error: null,
      isLoading: false,
      twoFactorToken: null,
      requiresTwoFactor: false,

      initializeAuth: async () => {
        try {
          const state = get();
          if (state.requiresTwoFactor) {
            set({ isInitializing: false });
            return;
          }
          if (state.token) {
            const userData = await state.fetchUserInfo();
            set({
              isAuthenticated: !!userData,
              isInitializing: false,
            });
          } else {
            set({
              isAuthenticated: false,
              isInitializing: false,
            });
          }
        } catch (error) {
          set({
            isAuthenticated: false,
            isInitializing: false,
          });
        }
      },

      login: async (username: string, password: string) => {
        try {
          set({ isLoading: true, error: null });

          const response = await api.post<AuthResponse>('/users/login', {
            username,
            password,
          });

          if (response.data.requiresTwoFactor && response.data.data) {
            const data = response.data.data as {
              twoFactorToken: string;
              user: { id: number; username: string; name: string; isChildAccount: boolean };
            };
            set({
              twoFactorToken: data.twoFactorToken,
              requiresTwoFactor: true,
              isLoading: false,
              error: null,
            });
            return;
          }

          if (!response.data.success || !response.data.data) {
            throw new Error(response.data.message || 'Falha na autenticação');
          }

          const { token, refreshToken, user } = response.data.data as {
            token: string;
            refreshToken: string;
            user: User;
          };

          // First set the token so subsequent API calls can use it
          set({
            token,
            refreshToken,
            user,
            isAuthenticated: true,
          });

          // Now that the token is set, fetch the complete user data
          try {
            // Configure the API client with the new token
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            const userResponse = await api.get('/users/me');

            if (userResponse.data && userResponse.data.data) {
              // Update with the complete user data
              set({
                user: userResponse.data.data,
                isLoading: false,
                error: null,
              });
            } else {
              // Keep the original user data if /me fails
              set({
                isLoading: false,
                error: null,
              });
            }
          } catch (userError) {
            // Still consider login successful even if /me fails
            set({
              isLoading: false,
              error: null,
            });
          }
        } catch (error: any) {
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.response?.data?.error || error.message,
          });
          throw error;
        }
      },

      completeTwoFactorLogin: async (twoFactorToken: string, code: string) => {
        try {
          set({ isLoading: true, error: null });

          const response = await api.post<TwoFactorLoginResponse>('/users/login/2fa', {
            twoFactorToken,
            code,
          });

          if (!response.data.success || !response.data.data) {
            throw new Error(response.data.message || 'Código 2FA inválido');
          }

          const { token, refreshToken, user } = response.data.data;

          set({
            token,
            refreshToken,
            user,
            isAuthenticated: true,
            twoFactorToken: null,
            requiresTwoFactor: false,
          });

          // Fetch complete user data
          try {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const userResponse = await api.get('/users/me');
            if (userResponse.data && userResponse.data.data) {
              set({ user: userResponse.data.data, isLoading: false, error: null });
            } else {
              set({ isLoading: false, error: null });
            }
          } catch {
            set({ isLoading: false, error: null });
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.error || error.message,
          });
          throw error;
        }
      },

      clearTwoFactor: () => {
        set({
          twoFactorToken: null,
          requiresTwoFactor: false,
          isLoading: false,
          error: null,
        });
      },

      register: async (data: RegisterRequest) => {
        try {
          set({ isLoading: true, error: null });

          const response = await api.post<AuthResponse>('/users/register', data);

          if (!response.data.success) {
            throw response.data;
          }

          set({ isLoading: false, error: null });
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.error || error.message,
          });
          throw error;
        }
      },

      logout: (message?: string) => {
        // Clear auth state
        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          error: null,
          isLoading: false,
          isSessionExpired: message === 'session_expired',
          twoFactorToken: null,
          requiresTwoFactor: false,
        });

        // Clear storage
        localStorage.removeItem('auth-storage');
        sessionStorage.clear();

        // Clear query cache
        if (window.queryClient) {
          window.queryClient.clear();
        }
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();

        if (!refreshToken) return null;

        try {
          const response = await api.post<AuthResponse>('/users/refresh-token', {
            refreshToken,
          });

          if (!response.data.success || !response.data.data) {
            throw new Error(response.data.message || 'Falha ao renovar token');
          }

          const { token: newToken, refreshToken: newRefreshToken } = response.data.data;

          set({
            token: newToken,
            refreshToken: newRefreshToken,
          });

          return newToken;
        } catch (error) {
          // Logout with session expiration
          get().logout('session_expired');
          return null;
        }
      },

      clearError: () => set({ error: null }),

      clearSessionExpired: () => set({ isSessionExpired: false }),

      fetchUserInfo: async () => {
        try {
          const response = await api.get<{ success: boolean; data: User }>('/users/me');

          if (response.data.success && response.data.data) {
            const userData = response.data.data;
            set({ user: userData });
            return userData;
          }
          return null;
        } catch (error) {
          if ((error as any)?.response?.status === 401 && !get().requiresTwoFactor) {
            get().logout('session_expired');
          }
          return null;
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: {
        getItem: name => {
          const value = localStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: name => {
          localStorage.removeItem(name);
        },
      },
      partialize: state => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
